"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getChartOptions = exports.getTreemapAttributes = exports.getDefaultTreemapAttributes = exports.getHeatmapDataClasses = exports.DEFAULT_HEATMAP_COLOR_INDEX = exports.HIGHCHARTS_PRECISION = exports.HEAT_MAP_CATEGORIES_COUNT = void 0;
var tslib_1 = require("tslib");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var interfaces_1 = require("../../../interfaces");
var dimensions_1 = require("../../constants/dimensions");
var executionResultHelper_1 = require("../_util/executionResultHelper");
var common_1 = require("../_util/common");
var dualAxis_1 = require("./dualAxis");
var comboChartOptions_1 = require("../comboChart/comboChartOptions");
var extendedStackingChartOptions_1 = require("./extendedStackingChartOptions");
var colorFactory_1 = require("./colorFactory");
var helpers_1 = require("../_chartCreators/helpers");
var chartCapabilities_1 = require("./chartCapabilities");
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var isUndefined_1 = tslib_1.__importDefault(require("lodash/isUndefined"));
var without_1 = tslib_1.__importDefault(require("lodash/without"));
var chartSeries_1 = require("./chartSeries");
var chartTooltips_1 = require("./chartTooltips");
var chartDrilling_1 = require("./chartDrilling");
var chartAxes_1 = require("./chartAxes");
var isAreaChartStackingEnabled = function (options) {
    var type = options.type, stacking = options.stacking, stackMeasures = options.stackMeasures;
    if (!common_1.isAreaChart(type)) {
        return false;
    }
    if (isUndefined_1.default(stackMeasures)) {
        return stacking || isUndefined_1.default(stacking);
    }
    return stackMeasures;
};
function getCategories(type, measureGroup, viewByAttribute, stackByAttribute) {
    if (common_1.isHeatmap(type)) {
        return [
            viewByAttribute ? viewByAttribute.items.map(function (item) { return item.attributeHeaderItem.name; }) : [""],
            stackByAttribute
                ? stackByAttribute.items.map(function (item) { return item.attributeHeaderItem.name; })
                : [""],
        ];
    }
    if (common_1.isScatterPlot(type)) {
        return stackByAttribute
            ? stackByAttribute.items.map(function (item) { return item.attributeHeaderItem.name; })
            : [""];
    }
    // Categories make up bar/slice labels in charts. These usually match view by attribute values.
    // Measure only pie or treemap charts get categories from measure names
    if (viewByAttribute) {
        return viewByAttribute.items.map(function (_a) {
            var attributeHeaderItem = _a.attributeHeaderItem;
            return attributeHeaderItem.name;
        });
    }
    if (common_1.isOneOfTypes(type, chartCapabilities_1.multiMeasuresAlternatingTypes)) {
        // Pie or Treemap chart with measures only (no viewByAttribute) needs to list
        return measureGroup.items.map(function (wrappedMeasure) { return common_1.unwrap(wrappedMeasure).name; });
        // Pie chart categories are later sorted by seriesItem pointValue
    }
    return [];
}
function getStackingConfig(stackByAttribute, options) {
    var type = options.type, stackMeasures = options.stackMeasures, stackMeasuresToPercent = options.stackMeasuresToPercent;
    var stackingValue = stackMeasuresToPercent ? "percent" : "normal";
    var supportsStacking = !common_1.isOneOfTypes(type, chartCapabilities_1.unsupportedStackingTypes);
    /**
     * we should enable stacking for one of the following cases :
     * 1) If stackby attribute have been set and chart supports stacking
     * 2) If chart is an area chart and stacking is enabled (stackBy attribute doesn't matter)
     * 3) If chart is column/bar chart and 'Stack Measures' is enabled
     */
    var isStackByChart = stackByAttribute && supportsStacking;
    var isAreaChartWithEnabledStacking = isAreaChartStackingEnabled(options);
    if (isStackByChart || isAreaChartWithEnabledStacking || stackMeasures || stackMeasuresToPercent) {
        return stackingValue;
    }
    return null; // no stacking
}
exports.HEAT_MAP_CATEGORIES_COUNT = 7;
exports.HIGHCHARTS_PRECISION = 15;
exports.DEFAULT_HEATMAP_COLOR_INDEX = 1;
function getHeatmapDataClasses(series, colorStrategy) {
    var _a, _b;
    if (series === void 0) { series = []; }
    var values = without_1.default(((_b = (_a = series[0]) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : []).map(function (item) { return item.value; }), null, undefined, NaN);
    if (isEmpty_1.default(values)) {
        return [];
    }
    var min = Math.min.apply(Math, values);
    var max = Math.max.apply(Math, values);
    var safeMin = parseFloat(Number(min).toPrecision(exports.HIGHCHARTS_PRECISION));
    var safeMax = parseFloat(Number(max).toPrecision(exports.HIGHCHARTS_PRECISION));
    var dataClasses = [];
    if (min === max) {
        dataClasses.push({
            from: min,
            to: max,
            color: colorStrategy.getColorByIndex(exports.DEFAULT_HEATMAP_COLOR_INDEX),
        });
    }
    else {
        var step = (safeMax - safeMin) / exports.HEAT_MAP_CATEGORIES_COUNT;
        var currentSum = safeMin;
        for (var i = 0; i < exports.HEAT_MAP_CATEGORIES_COUNT; i += 1) {
            dataClasses.push({
                from: currentSum,
                to: i === exports.HEAT_MAP_CATEGORIES_COUNT - 1 ? safeMax : currentSum + step,
                color: colorStrategy.getColorByIndex(i),
            });
            currentSum += step;
        }
    }
    return dataClasses;
}
exports.getHeatmapDataClasses = getHeatmapDataClasses;
function getDefaultTreemapAttributes(dv) {
    var dimensions = dv.meta().dimensions();
    var attributeHeaderItems = dv.meta().attributeHeaders();
    var viewByAttribute = executionResultHelper_1.findAttributeInDimension(dimensions[dimensions_1.STACK_BY_DIMENSION_INDEX], attributeHeaderItems[dimensions_1.STACK_BY_DIMENSION_INDEX]);
    var stackByAttribute = executionResultHelper_1.findAttributeInDimension(dimensions[dimensions_1.STACK_BY_DIMENSION_INDEX], attributeHeaderItems[dimensions_1.STACK_BY_DIMENSION_INDEX], 1);
    if (!viewByAttribute) {
        viewByAttribute = executionResultHelper_1.findAttributeInDimension(dimensions[dimensions_1.VIEW_BY_DIMENSION_INDEX], attributeHeaderItems[dimensions_1.VIEW_BY_DIMENSION_INDEX]);
    }
    return {
        viewByAttribute: viewByAttribute,
        stackByAttribute: stackByAttribute,
    };
}
exports.getDefaultTreemapAttributes = getDefaultTreemapAttributes;
function getTreemapAttributes(dv) {
    if (!dv.def().hasBuckets()) {
        // without mdObject cant distinguish 1M 1Vb 0Sb and 1M 0Vb 1Sb
        return getDefaultTreemapAttributes(dv);
    }
    var dimensions = dv.meta().dimensions();
    var attributeHeaderItems = dv.meta().attributeHeaders();
    if (dv.def().isBucketEmpty(sdk_ui_1.BucketNames.SEGMENT)) {
        if (dv.def().isBucketEmpty(sdk_ui_1.BucketNames.VIEW)) {
            return {
                viewByAttribute: null,
                stackByAttribute: null,
            };
        }
        return {
            viewByAttribute: executionResultHelper_1.findAttributeInDimension(dimensions[dimensions_1.VIEW_BY_DIMENSION_INDEX], attributeHeaderItems[dimensions_1.VIEW_BY_DIMENSION_INDEX]),
            stackByAttribute: null,
        };
    }
    if (dv.def().isBucketEmpty(sdk_ui_1.BucketNames.VIEW)) {
        return {
            viewByAttribute: null,
            stackByAttribute: executionResultHelper_1.findAttributeInDimension(dimensions[dimensions_1.VIEW_BY_DIMENSION_INDEX], attributeHeaderItems[dimensions_1.VIEW_BY_DIMENSION_INDEX]),
        };
    }
    return {
        viewByAttribute: executionResultHelper_1.findAttributeInDimension(dimensions[dimensions_1.STACK_BY_DIMENSION_INDEX], attributeHeaderItems[dimensions_1.STACK_BY_DIMENSION_INDEX]),
        stackByAttribute: executionResultHelper_1.findAttributeInDimension(dimensions[dimensions_1.STACK_BY_DIMENSION_INDEX], attributeHeaderItems[dimensions_1.STACK_BY_DIMENSION_INDEX], 1),
    };
}
exports.getTreemapAttributes = getTreemapAttributes;
function defaultChartedAttributeDiscovery(dv) {
    var attributeHeaderItems = dv.meta().attributeHeaders();
    var dimensions = dv.meta().dimensions();
    var isViewByTwoAttributes = attributeHeaderItems[dimensions_1.VIEW_BY_DIMENSION_INDEX] &&
        attributeHeaderItems[dimensions_1.VIEW_BY_DIMENSION_INDEX].length === interfaces_1.ViewByAttributesLimit;
    var viewByParentAttribute;
    var viewByAttribute = executionResultHelper_1.findAttributeInDimension(dimensions[dimensions_1.VIEW_BY_DIMENSION_INDEX], attributeHeaderItems[dimensions_1.VIEW_BY_DIMENSION_INDEX], isViewByTwoAttributes ? dimensions_1.PRIMARY_ATTRIBUTE_INDEX : undefined);
    var stackByAttribute = executionResultHelper_1.findAttributeInDimension(dimensions[dimensions_1.STACK_BY_DIMENSION_INDEX], attributeHeaderItems[dimensions_1.STACK_BY_DIMENSION_INDEX]);
    if (isViewByTwoAttributes) {
        viewByParentAttribute = executionResultHelper_1.findAttributeInDimension(dimensions[dimensions_1.VIEW_BY_DIMENSION_INDEX], attributeHeaderItems[dimensions_1.VIEW_BY_DIMENSION_INDEX], dimensions_1.PARENT_ATTRIBUTE_INDEX);
    }
    return {
        viewByAttribute: viewByAttribute,
        viewByParentAttribute: viewByParentAttribute,
        stackByAttribute: stackByAttribute,
        isViewByTwoAttributes: isViewByTwoAttributes,
    };
}
function chartedAttributeDiscovery(dv, chartType) {
    if (common_1.isTreemap(chartType)) {
        return getTreemapAttributes(dv);
    }
    return defaultChartedAttributeDiscovery(dv);
}
function getLegendLabel(type, viewByAttribute, stackByAttribute) {
    var _a, _b, _c;
    var legendLabel;
    if (common_1.isTreemap(type)) {
        legendLabel = (_a = viewByAttribute === null || viewByAttribute === void 0 ? void 0 : viewByAttribute.formOf) === null || _a === void 0 ? void 0 : _a.name;
    }
    else if (common_1.isOneOfTypes(type, chartCapabilities_1.sortedByMeasureTypes) && viewByAttribute) {
        legendLabel = (_b = viewByAttribute === null || viewByAttribute === void 0 ? void 0 : viewByAttribute.formOf) === null || _b === void 0 ? void 0 : _b.name;
    }
    else {
        legendLabel = (_c = stackByAttribute === null || stackByAttribute === void 0 ? void 0 : stackByAttribute.formOf) === null || _c === void 0 ? void 0 : _c.name;
    }
    return legendLabel;
}
function getChartOptions(dataView, chartConfig, drillableItems, theme) {
    var _a, _b, _c;
    var dv = sdk_ui_1.DataViewFacade.for(dataView);
    var dimensions = dv.meta().dimensions();
    var config = dualAxis_1.setMeasuresToSecondaryAxis(chartConfig, dv);
    ts_invariant_1.default(config && common_1.isChartSupported(config.type), "config.type must be defined and match one of supported chart types: " + common_1.stringifyChartTypes() + ", got: " + config.type);
    var type = config.type, chart = config.chart;
    var _d = chartedAttributeDiscovery(dv, type), viewByAttribute = _d.viewByAttribute, viewByParentAttribute = _d.viewByParentAttribute, stackByAttribute = _d.stackByAttribute, _e = _d.isViewByTwoAttributes, isViewByTwoAttributes = _e === void 0 ? false : _e;
    var colorStrategy = colorFactory_1.ColorFactory.getColorStrategy(config.colorPalette, config.colorMapping, viewByAttribute, stackByAttribute, dv, type, theme);
    var gridEnabled = (_b = (_a = config === null || config === void 0 ? void 0 : config.grid) === null || _a === void 0 ? void 0 : _a.enabled) !== null && _b !== void 0 ? _b : true;
    var stacking = getStackingConfig(stackByAttribute, config);
    var measureGroup = executionResultHelper_1.findMeasureGroupInDimensions(dimensions);
    var xAxes = chartAxes_1.getXAxes(dv, config, measureGroup, viewByAttribute);
    var yAxes = chartAxes_1.getYAxes(dv, config, measureGroup, stackByAttribute);
    var seriesWithoutDrillability = chartSeries_1.getSeries(dv, measureGroup, viewByAttribute, stackByAttribute, type, colorStrategy, theme);
    var drillableSeries = chartDrilling_1.getDrillableSeries(dv, seriesWithoutDrillability, drillableItems, [viewByAttribute, viewByParentAttribute], stackByAttribute, type);
    var series = chartAxes_1.assignYAxes(drillableSeries, yAxes);
    var categories = viewByParentAttribute
        ? extendedStackingChartOptions_1.getCategoriesForTwoAttributes(viewByAttribute, viewByParentAttribute)
        : getCategories(type, measureGroup, viewByAttribute, stackByAttribute);
    // Pie charts dataPoints are sorted by default by value in descending order
    if (common_1.isOneOfTypes(type, chartCapabilities_1.sortedByMeasureTypes)) {
        var dataPoints_1 = series[0].data;
        var indexSortOrder_1 = [];
        var sortedDataPoints = dataPoints_1
            .sort(function (pointDataA, pointDataB) {
            if (pointDataA.y === pointDataB.y) {
                return 0;
            }
            return pointDataB.y - pointDataA.y;
        })
            .map(function (dataPoint, dataPointIndex) {
            // Legend index equals original dataPoint index
            indexSortOrder_1.push(dataPoint.legendIndex);
            return tslib_1.__assign(tslib_1.__assign({}, dataPoint), { color: dataPoints_1[dataPointIndex].color, legendIndex: dataPointIndex });
        });
        // categories need to be sorted in exactly the same order as dataPoints
        categories = categories.map(function (_category, dataPointIndex) { return categories[indexSortOrder_1[dataPointIndex]]; });
        series[0].data = sortedDataPoints;
    }
    var colorAssignments = colorStrategy.getColorAssignment();
    var colorPalette = config.colorPalette;
    var _f = helpers_1.getChartProperties(config, type), xAxisProps = _f.xAxisProps, yAxisProps = _f.yAxisProps, secondary_xAxisProps = _f.secondary_xAxisProps, secondary_yAxisProps = _f.secondary_yAxisProps;
    if (common_1.isComboChart(type)) {
        var comboSeries = comboChartOptions_1.getComboChartSeries(config, measureGroup, series, dv);
        var canStackInPercent = comboChartOptions_1.canComboChartBeStackedInPercent(comboSeries);
        return {
            type: type,
            xAxes: xAxes,
            yAxes: yAxes,
            stacking: comboChartOptions_1.getComboChartStackingConfig(config, comboSeries, stacking),
            legendLayout: config.legendLayout || "horizontal",
            actions: {
                tooltip: chartTooltips_1.buildTooltipFactory(viewByAttribute, type, tslib_1.__assign(tslib_1.__assign({}, config), { stackMeasuresToPercent: config.stackMeasuresToPercent && canStackInPercent })),
            },
            grid: {
                enabled: gridEnabled,
            },
            data: {
                series: comboSeries,
                categories: categories,
            },
            xAxisProps: xAxisProps,
            yAxisProps: yAxisProps,
            secondary_yAxisProps: secondary_yAxisProps,
            colorAssignments: colorAssignments,
            colorPalette: colorPalette,
            forceDisableDrillOnAxes: chartConfig.forceDisableDrillOnAxes,
        };
    }
    if (common_1.isScatterPlot(type)) {
        var _g = helpers_1.getChartProperties(config, type), xAxisProps_1 = _g.xAxisProps, yAxisProps_1 = _g.yAxisProps;
        var measures = [
            measureGroup.items[0] ? measureGroup.items[0] : null,
            measureGroup.items[1] ? measureGroup.items[1] : null,
        ];
        if (dv.def().isBucketEmpty(sdk_ui_1.BucketNames.MEASURES)) {
            measures = [null, measureGroup.items[0] ? measureGroup.items[0] : null];
        }
        return {
            type: type,
            stacking: stacking,
            legendLayout: "horizontal",
            yAxes: yAxes,
            xAxes: xAxes,
            data: {
                series: series,
                categories: categories,
            },
            actions: {
                tooltip: chartTooltips_1.generateTooltipXYFn(measures, stackByAttribute, config),
            },
            grid: {
                enabled: gridEnabled,
            },
            xAxisProps: xAxisProps_1,
            yAxisProps: yAxisProps_1,
            colorAssignments: colorAssignments,
            colorPalette: colorPalette,
            forceDisableDrillOnAxes: chartConfig.forceDisableDrillOnAxes,
        };
    }
    if (common_1.isHeatmap(type)) {
        var _h = helpers_1.getChartProperties(config, type), xAxisProps_2 = _h.xAxisProps, yAxisProps_2 = _h.yAxisProps;
        return {
            type: type,
            stacking: null,
            legendLayout: "horizontal",
            legendLabel: (_c = common_1.unwrap(measureGroup === null || measureGroup === void 0 ? void 0 : measureGroup.items[0])) === null || _c === void 0 ? void 0 : _c.name,
            title: {
                x: viewByAttribute ? viewByAttribute.name : "",
                y: stackByAttribute ? stackByAttribute.name : "",
                format: common_1.unwrap(measureGroup.items[0]).format,
            },
            xAxes: xAxes,
            yAxes: yAxes,
            data: {
                series: series,
                categories: categories,
            },
            actions: {
                tooltip: chartTooltips_1.generateTooltipHeatmapFn(viewByAttribute, stackByAttribute, config),
            },
            grid: {
                enabled: false,
            },
            colorAxis: {
                dataClasses: getHeatmapDataClasses(series, colorStrategy),
            },
            xAxisProps: xAxisProps_2,
            yAxisProps: yAxisProps_2,
            colorAssignments: colorAssignments,
            colorPalette: colorPalette,
            forceDisableDrillOnAxes: chartConfig.forceDisableDrillOnAxes,
        };
    }
    if (common_1.isBubbleChart(type)) {
        var measures = [];
        var measureGroupCopy = cloneDeep_1.default(measureGroup);
        var _j = helpers_1.getChartProperties(config, type), xAxisProps_3 = _j.xAxisProps, yAxisProps_3 = _j.yAxisProps;
        if (!dv.def().isBucketEmpty(sdk_ui_1.BucketNames.MEASURES)) {
            measures.push(measureGroup.items[0] ? measureGroupCopy.items.shift() : null);
        }
        else {
            measures.push(null);
        }
        if (!dv.def().isBucketEmpty(sdk_ui_1.BucketNames.SECONDARY_MEASURES)) {
            measures.push(measureGroup.items[0] ? measureGroupCopy.items.shift() : null);
        }
        else {
            measures.push(null);
        }
        if (!dv.def().isBucketEmpty(sdk_ui_1.BucketNames.TERTIARY_MEASURES)) {
            measures.push(measureGroup.items[0] ? measureGroupCopy.items.shift() : null);
        }
        else {
            measures.push(null);
        }
        return {
            type: type,
            stacking: stacking,
            hasViewByAttribute: Boolean(stackByAttribute),
            legendLayout: "horizontal",
            legendLabel: getLegendLabel(type, viewByAttribute, stackByAttribute),
            yAxes: yAxes,
            xAxes: xAxes,
            data: {
                series: series,
                categories: [[""]],
            },
            actions: {
                tooltip: chartTooltips_1.generateTooltipXYFn(measures, stackByAttribute, config),
            },
            grid: {
                enabled: gridEnabled,
            },
            xAxisProps: xAxisProps_3,
            yAxisProps: yAxisProps_3,
            colorAssignments: colorAssignments,
            colorPalette: colorPalette,
            forceDisableDrillOnAxes: chartConfig.forceDisableDrillOnAxes,
        };
    }
    var isDualAxis = yAxes.length === 2;
    var measure;
    /**
     * Because of the problem described in TNT-16, we decided to change the visual of the tooltip.
     * If the visualization contains stack by attribute, it is possible to have just one measure.
     * Therefore the first measure from measureGroup is used.
     */
    if (!dv.def().isBucketEmpty(sdk_ui_1.BucketNames.MEASURES)) {
        measure = tslib_1.__assign({}, measureGroup.items[0]);
    }
    var tooltipFactory = chartTooltips_1.getTooltipFactory(isViewByTwoAttributes, viewByAttribute, viewByParentAttribute, stackByAttribute, measure, config, isDualAxis);
    return {
        type: type,
        stacking: stacking,
        hasStackByAttribute: Boolean(stackByAttribute),
        hasViewByAttribute: Boolean(viewByAttribute),
        legendLayout: config.legendLayout || "horizontal",
        legendLabel: getLegendLabel(type, viewByAttribute, stackByAttribute),
        xAxes: xAxes,
        yAxes: yAxes,
        data: {
            series: series,
            categories: categories,
        },
        actions: {
            tooltip: tooltipFactory,
        },
        grid: {
            enabled: gridEnabled,
        },
        xAxisProps: xAxisProps,
        yAxisProps: yAxisProps,
        secondary_xAxisProps: secondary_xAxisProps,
        secondary_yAxisProps: secondary_yAxisProps,
        colorAssignments: colorAssignments,
        colorPalette: colorPalette,
        isViewByTwoAttributes: isViewByTwoAttributes,
        forceDisableDrillOnAxes: chartConfig.forceDisableDrillOnAxes,
        verticalAlign: chart === null || chart === void 0 ? void 0 : chart.verticalAlign,
    };
}
exports.getChartOptions = getChartOptions;
//# sourceMappingURL=chartOptionsBuilder.js.map